// src/pinball/InputHandler.ts - Keyboard and touch input handling

export interface InputState {
  leftFlipper: boolean;
  rightFlipper: boolean;
  plungerHeld: boolean;
  pausePressed: boolean;
}

export class InputHandler {
  private state: InputState;
  private previousState: InputState;
  private isMobile: boolean;
  private touchZonesVisible: boolean;
  private canvas: HTMLElement | null;
  private activeTouches: Map<number, 'left' | 'right' | 'plunger'>;

  // Callbacks
  private onFlipperActivate?: (side: 'left' | 'right') => void;
  private onFlipperRelease?: (side: 'left' | 'right') => void;
  private onPlungerCharge?: () => void;
  private onPlungerRelease?: () => void;
  private onPause?: () => void;
  private onResetBall?: () => void;

  constructor() {
    this.state = { leftFlipper: false, rightFlipper: false, plungerHeld: false, pausePressed: false };
    this.previousState = { ...this.state };
    this.isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    this.touchZonesVisible = this.isMobile;
    this.canvas = null;
    this.activeTouches = new Map();
  }

  initialize(canvas: HTMLElement): void {
    this.canvas = canvas;

    // Keyboard events
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('keyup', this.handleKeyUp);

    // Touch events
    if (this.isMobile) {
      canvas.addEventListener('touchstart', this.handleTouchStart, { passive: false });
      canvas.addEventListener('touchend', this.handleTouchEnd, { passive: false });
      canvas.addEventListener('touchcancel', this.handleTouchEnd, { passive: false });
    }
  }

  destroy(): void {
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('keyup', this.handleKeyUp);

    if (this.canvas && this.isMobile) {
      this.canvas.removeEventListener('touchstart', this.handleTouchStart);
      this.canvas.removeEventListener('touchend', this.handleTouchEnd);
      this.canvas.removeEventListener('touchcancel', this.handleTouchEnd);
    }
  }

  setCallbacks(callbacks: {
    onFlipperActivate?: (side: 'left' | 'right') => void;
    onFlipperRelease?: (side: 'left' | 'right') => void;
    onPlungerCharge?: () => void;
    onPlungerRelease?: () => void;
    onPause?: () => void;
    onResetBall?: () => void;
  }): void {
    this.onFlipperActivate = callbacks.onFlipperActivate;
    this.onFlipperRelease = callbacks.onFlipperRelease;
    this.onPlungerCharge = callbacks.onPlungerCharge;
    this.onPlungerRelease = callbacks.onPlungerRelease;
    this.onPause = callbacks.onPause;
    this.onResetBall = callbacks.onResetBall;
  }

  pollInputState(): InputState {
    const current = { ...this.state };
    this.previousState = current;
    return current;
  }

  getIsMobile(): boolean {
    return this.isMobile;
  }

  getTouchZonesVisible(): boolean {
    return this.touchZonesVisible;
  }

  private handleKeyDown = (e: KeyboardEvent): void => {
    switch (e.key) {
      case 'Shift':
        if (e.location === KeyboardEvent.DOM_KEY_LOCATION_LEFT) {
          if (!this.state.leftFlipper) {
            this.state.leftFlipper = true;
            this.onFlipperActivate?.('left');
          }
        } else if (e.location === KeyboardEvent.DOM_KEY_LOCATION_RIGHT) {
          if (!this.state.rightFlipper) {
            this.state.rightFlipper = true;
            this.onFlipperActivate?.('right');
          }
        }
        break;
      case 'z':
      case 'Z':
      case 'ArrowLeft':
        e.preventDefault();
        if (!this.state.leftFlipper) {
          this.state.leftFlipper = true;
          this.onFlipperActivate?.('left');
        }
        break;
      case '/':
      case 'ArrowRight':
        e.preventDefault();
        if (!this.state.rightFlipper) {
          this.state.rightFlipper = true;
          this.onFlipperActivate?.('right');
        }
        break;
      case ' ':
      case 'ArrowDown':
        e.preventDefault();
        if (!this.state.plungerHeld) {
          this.state.plungerHeld = true;
          this.onPlungerCharge?.();
        }
        break;
      case 'Escape':
        this.state.pausePressed = true;
        this.onPause?.();
        break;
      case 'r':
      case 'R':
        // Debug: reset ball to launcher
        this.onResetBall?.();
        break;
    }
  };

  private handleKeyUp = (e: KeyboardEvent): void => {
    switch (e.key) {
      case 'Shift':
        if (e.location === KeyboardEvent.DOM_KEY_LOCATION_LEFT) {
          this.state.leftFlipper = false;
          this.onFlipperRelease?.('left');
        } else if (e.location === KeyboardEvent.DOM_KEY_LOCATION_RIGHT) {
          this.state.rightFlipper = false;
          this.onFlipperRelease?.('right');
        }
        break;
      case 'z':
      case 'Z':
      case 'ArrowLeft':
        this.state.leftFlipper = false;
        this.onFlipperRelease?.('left');
        break;
      case '/':
      case 'ArrowRight':
        this.state.rightFlipper = false;
        this.onFlipperRelease?.('right');
        break;
      case ' ':
      case 'ArrowDown':
        if (this.state.plungerHeld) {
          this.state.plungerHeld = false;
          this.onPlungerRelease?.();
        }
        break;
      case 'Escape':
        this.state.pausePressed = false;
        break;
    }
  };

  private handleTouchStart = (e: TouchEvent): void => {
    e.preventDefault();
    const rect = this.canvas?.getBoundingClientRect();
    if (!rect) return;

    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      const x = touch.clientX - rect.left;
      const halfWidth = rect.width / 2;

      if (x < halfWidth) {
        this.activeTouches.set(touch.identifier, 'left');
        if (!this.state.leftFlipper) {
          this.state.leftFlipper = true;
          this.onFlipperActivate?.('left');
        }
      } else {
        this.activeTouches.set(touch.identifier, 'right');
        if (!this.state.rightFlipper) {
          this.state.rightFlipper = true;
          this.onFlipperActivate?.('right');
        }
      }
    }
  };

  private handleTouchEnd = (e: TouchEvent): void => {
    e.preventDefault();

    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      const side = this.activeTouches.get(touch.identifier);
      this.activeTouches.delete(touch.identifier);

      if (side === 'left') {
        // Check if any other touch is still on left
        const hasLeft = Array.from(this.activeTouches.values()).includes('left');
        if (!hasLeft) {
          this.state.leftFlipper = false;
          this.onFlipperRelease?.('left');
        }
      } else if (side === 'right') {
        const hasRight = Array.from(this.activeTouches.values()).includes('right');
        if (!hasRight) {
          this.state.rightFlipper = false;
          this.onFlipperRelease?.('right');
        }
      }
    }
  };
}
